import apiClient, { getIdToken, getPageSource } from "./apiClient";
import { config } from "./config";
import { handleTokenExpiration } from "./authUtils";
import { API_PATHS, jsonAuthHeaders, authHeader, POLLING, JOB_STATUS } from "./constants";

const API_BASE_URL = config.apiBaseUrl;

export interface ValueObj {
  field: string;
  aggregation: "sum" | "average" | "count" | "min" | "max";
}

// Plan API Types
export interface PlanStep {
  step_id: string;  // Supports base types and indexed variants: "smart_column", "smart_column_2", etc.
  enabled: boolean;
  reason: string;
  config?: { threshold?: number } | null;
}

export interface RCAPlan {
  steps: PlanStep[];
  summary: string;
}

export interface QuestionAnalysis {
  metric_focus: string;
  time_period: string;
  entities: string[];
  analysis_type: string;
}

export interface ColumnSemantics {
  columnName: string;
  meaning: string;
}

export interface TableColumnSemantics {
  tableName: string;
  columns: ColumnSemantics[];
}

export interface RCATableInput {
  table_name: string;
  headers: string[];
  rows: any[][];
}

export interface SmartMatchTable {
  headers: string[];
  rows: any[][];
}

// Column Description API types
export interface ColumnDescriptionRequest {
  headers: string[];
  sample_rows: any[][];
}

export interface ColumnDescriptionItem {
  column_name: string;
  meaning: string;
}

export interface ColumnDescriptionResponse {
  descriptions: ColumnDescriptionItem[];
  success: boolean;
  message: string;
}

export interface RCAPlanRequest {
  tables: RCATableInput[];
  user_question: string;
  column_semantics?: ColumnSemantics[];
}

export interface RCAPlanResponse {
  plan: RCAPlan;
  question_analysis: QuestionAnalysis;
  estimated_steps: number;
  warnings: string[];
}

export interface RCAAnalyzeRequest {
  tables: RCATableInput[];
  user_question: string;
  plan?: RCAPlan;
  original_plan?: RCAPlan;  // v2.3.0: Original plan for validation
  skip_validation?: boolean;  // v2.4.0: Skip validation when user clicks "Proceed Further"
}

export interface RCAJobResponse {
  job_id: string;
  status: string;
  message: string;
}

export interface GeneratedColumn {
  column_name: string;
  description: string;
}

export interface PlanExecution {
  steps_executed: string[];
  steps_skipped: string[];
}

export interface CalculatedColumn {
  name: string;
  formula: string;
  description: string;
}

// Pivot table configuration structure
export interface PivotTableConfig {
  rows: string[];
  columns: string[];
  values: ValueObj[];
  filters: string[];
  error?: string | null;
  calculated_columns?: CalculatedColumn[];
}

// Result for a single pivot table step (used in pivot_table_results map)
export interface PivotTableResult {
  config: PivotTableConfig;
  grouped_grid?: Array<Record<string, any>>;
  records?: any[];
}

export interface RCAResult {
  enriched_table: {
    headers: string[];
    rows: any[][];
  };
  // Legacy single pivot table (for backward compatibility)
  pivot_table: PivotTableConfig;
  // Legacy single grouped grid (for backward compatibility)
  grouped_grid?: Array<Record<string, any>>;
  // Multiple pivot tables keyed by step_id (e.g., "pivot_table", "pivot_table_2")
  pivot_table_results?: Record<string, PivotTableResult>;
  // Legacy HTML report (may be null when using JESS format)
  html_report?: string;
  summary: string;
  // v6.0.0: Excel-styled report in JESS format
  excel_styled_report?: Record<string, any>;
  output_format?: string; // "excel" or "html"
  generated_columns: GeneratedColumn[];
  root_causes_count: number;
  trends_count?: number;
  anomalies_count?: number;
  plan?: RCAPlan;
  plan_execution?: PlanExecution;
  // Smart match results keyed by matched table name
  smart_match_tables?: Record<string, SmartMatchTable>;
}

export interface RCAProgressUpdate {
  progress: number;
  phase: string;
  message: string;
  status: typeof JOB_STATUS.PROCESSING | typeof JOB_STATUS.COMPLETED | typeof JOB_STATUS.FAILED;
  result?: RCAResult;
  error?: string;
  timestamp?: string;
}

// NOTE: Plan Step Validation types removed in v2.3.0
// Validation now happens as a node in the backend LangGraph workflow
// See original_plan parameter in RCAAnalyzeRequest

export async function generateRCAPlan(
  request: RCAPlanRequest
): Promise<RCAPlanResponse> {
  const response = await apiClient.post<RCAPlanResponse>('/rca/plan', request);
  return response.data;
}

export async function startRCAAnalysis(
  request: RCAAnalyzeRequest,
  token?: string
): Promise<RCAJobResponse> {
  // If a pre-fetched token is supplied, pass it as a header override
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const response = await apiClient.post<RCAJobResponse>('/rca/analyze', request, { headers });
  return response.data;
}

export function getProgressSSEUrl(jobId: string): string {
  return `${API_BASE_URL}${API_PATHS.RCA_PROGRESS}/${encodeURIComponent(jobId)}`;
}

/**
 * Stream RCA progress updates using fetch with proper Authorization header.
 * EventSource doesn't support custom headers, so we use fetch with ReadableStream.
 *
 * NOTE: This function intentionally uses native fetch (not Axios) because
 * Axios does not support browser-side ReadableStream / SSE parsing.
 *
 * Includes retry logic (up to 3 attempts) for transient network errors,
 * which can occur in the Office.js WebView2 runtime.
 */
export async function streamRCAProgress(
  jobId: string,
  onProgress: (update: RCAProgressUpdate) => void,
  onError: (error: string) => void,
  abortSignal?: AbortSignal,
  token?: string
): Promise<void> {
  const authToken = token || await getIdToken();
  const url = getProgressSSEUrl(jobId);

  for (let attempt = 1; attempt <= POLLING.SSE_MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "text/event-stream",
          ...authHeader(authToken),
          "Cache-Control": "no-cache",
          "X-Page-Source": getPageSource(),
        },
        signal: abortSignal,
      });

      if (response.status === 401) {
        await handleTokenExpiration('Session expired during RCA progress');
        throw new Error('Session expired. Please login again.');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || errorData.detail || `Progress stream failed: ${response.statusText}`
        );
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("Failed to get response reader");
      }

      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages (lines ending with \n\n)
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6).trim();
            if (data) {
              try {
                const sanitized = data
                  .replace(/\bNaN\b/g, "null")
                  .replace(/\b-?Infinity\b/g, "null");
                const parsed: RCAProgressUpdate = JSON.parse(sanitized);
                onProgress(parsed);

                // If completed or failed, we're done
                if (parsed.status === JOB_STATUS.COMPLETED || parsed.status === JOB_STATUS.FAILED) {
                  reader.cancel();
                  return;
                }
              } catch (parseError) {
                console.error("Error parsing SSE data:", parseError, data);
              }
            }
          }
        }
      }

      // If we reach here, the stream ended normally
      return;
    } catch (err: any) {
      if (err.name === "AbortError") {
        console.log("RCA progress stream aborted");
        return;
      }

      // Only retry on network errors (TypeError: Failed to fetch), not HTTP errors
      const isNetworkError = err instanceof TypeError && err.message.includes("Failed to fetch");

      if (isNetworkError && attempt < POLLING.SSE_MAX_RETRIES) {
        console.warn(`RCA SSE connection attempt ${attempt}/${POLLING.SSE_MAX_RETRIES} failed, retrying in ${POLLING.SSE_RETRY_DELAY_MS}ms...`, err.message);
        await new Promise(resolve => setTimeout(resolve, POLLING.SSE_RETRY_DELAY_MS));
        continue;
      }

      console.error("RCA progress stream error:", err);
      onError(err.message || "Connection to analysis service lost");
      return;
    }
  }
}

// NOTE: validatePlanStepEdit function removed in v2.3.0
// Validation now happens as a node in the backend LangGraph workflow

/**
 * Get column descriptions from LLM based on headers and sample data.
 * This provides meaningful semantic descriptions for each column.
 */
export async function getColumnDescriptions(
  request: ColumnDescriptionRequest
): Promise<ColumnDescriptionResponse> {
  const response = await apiClient.post<ColumnDescriptionResponse>(
    '/rca/column-descriptions',
    request
  );
  return response.data;
}

const rcaApiService = {
  generateRCAPlan,
  startRCAAnalysis,
  getProgressSSEUrl,
  streamRCAProgress,
  getColumnDescriptions,
};

export default rcaApiService;
